import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/** Templates (SPEC-20260830-1225) — hooks de dados da biblioteca. */

export type TemplateKind = 'work_item' | 'checklist'

/** Linha da biblioteca (sem o payload — a lista nao carrega a arvore). */
export type TemplateResumo = {
  id: string
  kind: TemplateKind
  name: string
  description: string
  workItemTypeId: string | null
  config: { resetStatus?: boolean } & Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/** Item do editor de template de checklist. */
export type ItemChecklistTemplate = {
  text: string
  /** Marcado: ao aplicar, esta linha vira um Item de Trabalho filho. */
  criaItem: boolean
  /** Configuração do item gerado — os mesmos campos do 'Converter em item'. */
  workItemTypeId?: string | null
  workItemStatusId?: string | null
  workItemPriorityId?: string | null
  companyAreaId?: string | null
  flowStepId?: string | null
  description?: string
}

async function corpo<T>(r: Response): Promise<T> {
  const d = (await r.json()) as T & { error?: { message?: string } }
  if (!r.ok) throw new Error(d.error?.message ?? 'Não foi possível concluir a operação.')
  return d
}

const CHAVE = ['templates'] as const

export function useTemplates(kind?: TemplateKind) {
  return useQuery({
    queryKey: [...CHAVE, kind ?? 'all'],
    queryFn: async () => {
      const r = await api.templates.$get({ query: kind ? { kind } : {} })
      return corpo<{ templates: TemplateResumo[] }>(r)
    },
  })
}

/** Um template COM payload (para o editor de checklist). */
export function useTemplate(id: string | null) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: [...CHAVE, 'um', id],
    queryFn: async () => {
      const r = await api.templates[':id'].$get({ param: { id: id! } })
      return corpo<{ template: TemplateResumo & { payload: unknown } }>(r)
    },
  })
}

function useInvalida() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: CHAVE })
}

/** Captura a cadeia de um Item de Trabalho como template. */
export function useSalvarComoTemplate() {
  const invalida = useInvalida()
  return useMutation({
    mutationFn: async (input: {
      workItemId: string
      name: string
      description: string
      incluirFilhos: boolean
      resetStatus: boolean
    }) => {
      const { workItemId, ...json } = input
      const r = await api.templates['from-work-item'][':workItemId'].$post({
        param: { workItemId },
        json: json as never,
      })
      return corpo<{ template: TemplateResumo; itens: number }>(r)
    },
    onSuccess: invalida,
  })
}

export function useSalvarTemplateChecklist() {
  const invalida = useInvalida()
  return useMutation({
    mutationFn: async (input: {
      id?: string | null
      name: string
      description: string
      checklist: { name: string; items: ItemChecklistTemplate[] }
    }) => {
      const { id, ...json } = input
      const r = id
        ? await api.templates[':id'].$patch({ param: { id }, json: json as never })
        : await api.templates.checklist.$post({ json: json as never })
      return corpo<{ template: TemplateResumo }>(r)
    },
    onSuccess: invalida,
  })
}

/**
 * Edita um template. Nome/descrição/resetStatus valem para os dois tipos;
 * `tree` só para template de Item de Trabalho (o servidor mescla por `ref`).
 */
export function useEditarTemplate() {
  const invalida = useInvalida()
  return useMutation({
    mutationFn: async (input: {
      id: string
      name?: string
      description?: string
      resetStatus?: boolean
    }) => {
      const { id, ...json } = input
      const r = await api.templates[':id'].$patch({ param: { id }, json: json as never })
      return corpo<{ template: TemplateResumo }>(r)
    },
    onSuccess: invalida,
  })
}

/**
 * Sessão de edição (SPEC-20260830-1530): abrir materializa o template como
 * Item de Trabalho de BASTIDOR e devolve o id — a partir daí quem edita é a
 * própria tela do Item de Trabalho.
 */
export function useAbrirSessaoTemplate(templateId: string | 'novo') {
  return useQuery({
    // `useQuery` e nao `useMutation` de proposito: o React Query DEDUPLICA por
    // queryKey, e sem isso as duas montagens do StrictMode abriam duas sessoes
    // em paralelo — ambas calculavam o mesmo `TPL-####` e uma estourava o
    // unique de codigo.
    queryKey: ['template-session', templateId],
    queryFn: async () => {
      const r = await api.templates[':id']['edit-session'].$post({ param: { id: templateId } })
      return corpo<{ workItemId: string; templateId: string | null }>(r)
    },
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/** Salva: captura o bastidor de volta para o template e descarta. */
export function useSalvarSessaoTemplate() {
  const invalida = useInvalida()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { templateId: string; workItemId: string; name?: string; description?: string }) => {
      const { templateId, ...json } = input
      const r = await api.templates[':id']['commit-session'].$post({ param: { id: templateId }, json: json as never })
      return corpo<{ template: TemplateResumo; itens: number }>(r)
    },
    onSuccess: () => {
      invalida()
      void qc.invalidateQueries({ queryKey: ['work-item'] })
    },
  })
}

/** Cancela: descarta o bastidor sem tocar no template. */
export function useCancelarSessaoTemplate() {
  return useMutation({
    mutationFn: async (input: { templateId: string; workItemId: string }) => {
      await corpo<{ ok: true }>(
        await api.templates[':id']['edit-session'].$delete({
          param: { id: input.templateId },
          query: { workItemId: input.workItemId },
        }),
      )
      return input.workItemId
    },
  })
}

export function useExcluirTemplate() {
  const invalida = useInvalida()
  return useMutation({
    mutationFn: async (id: string) => {
      await corpo<{ ok: true }>(await api.templates[':id'].$delete({ param: { id } }))
      return id
    },
    onSuccess: invalida,
  })
}

/**
 * Aplica um template. `work_item` cria a cadeia e devolve `rootId`;
 * `checklist` exige `workItemId` (o item que recebe) e devolve `workItemId`.
 */
export function useAplicarTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      title?: string
      parentWorkItemId?: string | null
      workItemId?: string
      /** Etapa da RAIZ — usado ao aplicar a partir de uma lane do Kanban. */
      flowStepId?: string | null
      /** Nome da lista criada (template de checklist). */
      checklistName?: string
      /** Quadro em que a cadeia nasce: vale a visibilidade dele (SPEC-20261007-0934-visibilidade-padrao-do-quadro). */
      kanbanBoardId?: string
    }) => {
      const { id, ...json } = input
      const r = await api.templates[':id'].apply.$post({ param: { id }, json: json as never })
      return corpo<{ rootId?: string; workItemId?: string; criados: number }>(r)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-items'] })
      void qc.invalidateQueries({ queryKey: ['work-item'] })
    },
  })
}
